/**
 * Typed wrapper over Firebase callables.
 *
 * Exists so that error handling happens once. A raw callable rejects with a
 * FunctionsError whose `code` is prefixed ("functions/permission-denied") and
 * whose `message` is whatever the backend threw — which, for our functions, is
 * already written for a person to read. `callError` turns that into something
 * a component can render without every component learning the shape.
 */

import { httpsCallable, FunctionsError } from 'firebase/functions';
import { functions } from './firebase';

export interface CallFailure {
  /** Bare code, without the "functions/" prefix: 'permission-denied'. */
  code: string;
  /** Safe to show a user — our backend writes these deliberately. */
  message: string;
}

export function isCallFailure(e: unknown): e is CallFailure {
  return typeof e === 'object' && e !== null && 'code' in e && 'message' in e;
}

/**
 * Strip the status code the SDK appends to a callable's message.
 *
 * Firebase turns our "Please request a new one." into "Please request a new
 * one. [403]". Harmless to a developer, and noise to a nurse who is already
 * having a bad morning — the backend writes these strings for her to read, so
 * they should reach her as written. Caught by looking at the rendered page;
 * no test would have flagged it, because the message was still "correct".
 */
function cleanMessage(message: string): string {
  // Any trailing bracketed number, not only three digits. A failure that
  // never reached the server is appended as "[0]", which slipped through a
  // \\d{3} pattern and was shown to a user as "internal [0]".
  return message.replace(/\s*\[\d{1,3}\]\s*$/, '').trim();
}

/** Shown in place of the bare word "INTERNAL" from an unhandled server error. */
export const SERVER_FAULT = 'Something went wrong on our side, not yours. Try again in a minute. If it keeps happening, tell StandingDocs support what you were doing.';

const UNREACHABLE = 'Could not reach the server. Check your connection and try again.';

/**
 * How long a call may take before it is given up: 30 seconds. The SDK's
 * own 70 seconds left a person looking at "Sending…" for over a minute and
 * then reading "deadline-exceeded"; our functions answer in a second or two
 * and a cold start in ten, so anything past 30 is the network, not us.
 */
const CALL_TIMEOUT_MS = 30_000;

function toFailure(e: unknown): CallFailure {
  if (e instanceof FunctionsError) {
    const code = e.code.replace(/^functions\//, '');
    // A timeout never reached a function, so there is no backend wording.
    if (code === 'deadline-exceeded') return { code: 'unavailable', message: UNREACHABLE };
    // An unhandled server error arrives as the bare word "INTERNAL", which
    // tells a person nothing. Our own errors always carry a sentence.
    if (code === 'internal' && /^internal$/i.test(cleanMessage(e.message))) return { code, message: SERVER_FAULT };
    return {
      code,
      message: cleanMessage(e.message),
    };
  }
  // A network failure never reached the function, so the backend's wording
  // does not apply and we supply our own.
  return { code: 'unavailable', message: UNREACHABLE };
}

/**
 * Leave out what is not set.
 *
 * The callable SDK sends `undefined` as `null`, and every backend schema
 * refuses null where it accepts a missing key ("slug: Expected string,
 * received null" on the New organization form, from an identifier left
 * empty). Dropping undefined keys before the call means a form can build
 * `{ slug: slug.trim() || undefined }` and have it mean "no slug", which is
 * what every form here already assumes.
 */
export function withoutUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map(withoutUndefined) as unknown as T;
  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v !== undefined) out[k] = withoutUndefined(v);
    }
    return out as T;
  }
  return value;
}

/**
 * `timeoutMs` is for the few calls that really do take minutes (reading a
 * 500 MB zip, importing a batch). Everything else keeps the 30 seconds.
 */
export async function call<Req, Res>(name: string, data: Req, options: { timeoutMs?: number } = {}): Promise<Res> {
  try {
    const fn = httpsCallable<Req, Res>(functions, name, { timeout: options.timeoutMs ?? CALL_TIMEOUT_MS });
    const result = await fn(withoutUndefined(data));
    return result.data;
  } catch (e) {
    throw toFailure(e);
  }
}
